/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IContentBlockAdditionalContentItemViewProps,
    IContentBlockAdditionalContentViewProps,
    IContentBlockViewProps
} from '@msdyn365-commerce-modules/content-block';
import { IImageSettings, IViewPort } from '@msdyn365-commerce/core';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import classnames from 'classnames';
import { IContentBlockConfig } from '../definition-extensions/content-block.ext.props.autogenerated';

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IContentBlockAdditionalContentViewProps) => {
    return (
        <Node {...additionalContent.additionalContentNode}>
            {additionalContent.additionalContentItems?.map((item: IContentBlockAdditionalContentItemViewProps) => {
                return (
                    <>
                        {item.heading}
                        <Node {...item.additionalContentItemContainer}>
                            {item.text}
                            <Node {...item.additionalContentItemLinks}>{item.links}</Node>
                        </Node>
                    </>
                );
            })}
        </Node>
    );
};

/**
 * Render View.
 * @param props - The view props.
 * @returns -The JSX Element.
 */
const contentBlockView: React.FC<IContentBlockViewProps> = props => {
    const {
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        title,
        text,
        links,
        image,
        imageLink,
        imageAriaLabel,
        additionalContent
    } = props;
    const config = props.config as IContentBlockConfig;

    // @ts-expect-error HTML element need to be clear on run time.
    const hasImage: boolean = image.props.src;
    const imageClass: string = hasImage ? `${detailsContainer.className} withImage` : `${detailsContainer.className} withoutImage`;
    let newImage: any;

    if (image) {
        const imageSettings: IImageSettings = { ...(image as any).props.imageSettings };

        if (imageSettings?.viewports) {
            Object.keys(imageSettings.viewports).forEach((k) => {
                const key = k as keyof IViewPort;

                if (imageSettings.viewports[key]?.q) {
                    const parametersToFilter = [
                        config.disableRemoteImageHandling && 'm',
                        'f'
                    ].filter(Boolean) as string[];
                    const parametersToAdd = [
                        config.disableRemoteImageHandling && 'm=0',
                        config.forceWebp && 'f=webp',
                        !config.forceWebp && 'f=png'
                    ].filter(Boolean) as string[];
                    const parameters = [
                        ...(imageSettings.viewports[key]!.q as string)
                            .split('&')
                            .filter((param) => !parametersToFilter.includes(param.split('=')[0])),
                        ...parametersToAdd
                    ].filter(Boolean) as string[];
                    imageSettings!.viewports[key]!.q = parameters.join('&');
                }
            });
            newImage = React.cloneElement(image as React.ReactElement, { imageSettings });
            imageContainer.className = classnames(imageContainer.className, {
                'disable-rih': config.disableRemoteImageHandling
            });
        }
    }

    if (imageLink) {
        const contentBlockAnchorTag = config.openImageLinkInNewTab && props.contentBlockAnchorTag
            ? {
                ...props.contentBlockAnchorTag,
                target: '_blank'
            } : props.contentBlockAnchorTag;

        return (
            <Module {...contentBlockContainer}>
                <Node
                    {...contentBlockAnchorTag}
                    href={imageLink}
                    className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                    aria-label={imageAriaLabel}
                >
                    <Node {...imageContainer}>{newImage || image}</Node>
                </Node>
                <Node {...detailsContainer}>
                    {title}
                    {text}
                    {links}
                    {additionalContent && renderAdditionalContent(additionalContent)}
                </Node>
            </Module>
        );
    }
    return (
        <Module {...contentBlockContainer}>
            <Node {...imageContainer}>{newImage || image}</Node>
            <Node className={imageClass}>
                {title}
                {text}
                {links}
                {additionalContent && renderAdditionalContent(additionalContent)}
            </Node>
        </Module>
    );
};

export default contentBlockView;
